import type { IrReport } from './types';

export const G2_IR_REPORTS: IrReport[] = [
  {
    key: 'G2R1',
    group: 'G2',
    rCode: 'R1',
    pillar: 'Growth Architecture',
    stage: 'Critical Risk Stage',
    tone: 'red',
    profile: 'Vision Builder',
    tagline: "A founder full of potential, now tasked with turning imagination into reality.",
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: "You are at the very beginning of your product journey. At this stage, you likely have a strong vision or concept, but you don't yet have a working prototype or MVP to validate your assumptions. This does not mean you're behind, it means you're in the founder-as-architect phase: defining the problem, imagining the solution, and laying down the blueprint for your first product. Investors and customers alike will not engage seriously until you've proven your ability to turn vision into a testable product. Your mission now is to translate your idea into something tangible, even if imperfect, to validate market demand. You are a Vision Builder, a founder full of potential, now tasked with turning imagination into reality.",
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          {
            kind: 'paragraph',
            text: "Your answers show several indicators of being at the critical risk stage for product development:",
          },
          {
            kind: 'bullets',
            items: [
              "You have an idea or concept, but no functional prototype.",
              "Customer problem validation may still be anecdotal or untested.",
              "No user testing, mockups, or prototypes have been created.",
              "Development resources (team, CTO, freelancers) may not yet be in place.",
            ],
          },
          {
            kind: 'paragraph',
            text: "This is typical for founders at the earliest stage, but it means you must now prioritize proof over vision.",
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: "Validate the Problem",
                sub: [
                  "Conduct at least 15–20 customer interviews",
                  "Document pain points in a structured way (Problem Statement Worksheet)",
                  "Define what success looks like for users",
                ],
              },
              {
                title: "Create a Simple Prototype (No-Code if Possible)",
                sub: [
                  "Use no-code tools (Bubble, Webflow, Figma) to create wireframes or clickable mockups",
                  "Focus on testing functionality, not perfection",
                  "Validate whether users understand and value your solution",
                ],
              },
              {
                title: "Build the MVP Roadmap",
                sub: [
                  "Define your \"must-have\" vs \"nice-to-have\" features",
                  "Prioritize 1–2 critical features to test in your MVP",
                  "Outline development options: in-house, freelancers, or agency",
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: "At this stage, your platform provides starter tools to move from idea to prototype:",
          },
          {
            kind: 'bullets',
            items: [
              "MVP Builder Lite: Lean Canvas + Problem Validation Worksheet",
              "Prototype Planning Template: map features and prioritize",
              "No-Code Resource Guide: recommended tools and platforms",
              "Customer Interview Script Pack: to standardize conversations",
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: "You are still early, too early for investors, but not too early for validation. Founders who succeed at this stage are those who test fast, fail fast, and learn fast. The goal is not to build a perfect product but to prove that customers care. Once you validate demand, you can attract technical talent, capital, and early adopters.",
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: "You are a Vision Builder.", tone: 'blue' },
          {
            kind: 'paragraph',
            text: "Your idea is your foundation. Now it's time to build something tangible.",
          },
          {
            kind: 'bullets',
            items: [
              "Conduct 15+ customer interviews this month",
              "Use the MVP Builder Lite toolkit to map your features",
              "Build your first clickable prototype using a no-code tool",
              "Schedule a Product Validation Session with a Conncct advisor",
            ],
          },
          {
            kind: 'callout',
            text: "Your next milestone: progress to G2R2 Prototype Starter, where your idea evolves into a working prototype ready for testing.",
            tone: 'green',
          },
        ],
      },
    ],
  },
  {
    key: 'G2R2',
    group: 'G2',
    rCode: 'R2',
    pillar: 'Growth Architecture',
    stage: 'Very High Risk Stage',
    tone: 'orange',
    profile: 'Prototype Starter',
    tagline: "You've started to build, but now it's about proving.",
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: "You've taken the first tangible step beyond vision: you now have a prototype or early mockup of your product. That's a huge milestone: many ideas never reach this stage. However, at this point your risk remains very high. A prototype by itself doesn't prove product–market fit. You need to use it as a tool for validation and learning, not as the final product. Your next mission is to test, iterate, and collect evidence that your solution truly resonates with users. You are a Prototype Starter, you've started to build, but now it's about proving.",
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          {
            kind: 'paragraph',
            text: "The diagnostic reveals clear markers of your stage:",
          },
          {
            kind: 'bullets',
            items: [
              "You've created a basic prototype, mockup, or clickable demo.",
              "Technical validation may exist (it \"works\"), but customer validation is weak or absent.",
              "You may still be refining feature priorities.",
              "No structured testing framework yet for feedback.",
            ],
          },
          {
            kind: 'paragraph',
            text: "This is progress, but investors and customers will still see your work as unproven until you collect hard evidence.",
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: "Test the Prototype with Real Users",
                sub: [
                  "Recruit 10–20 potential users to try the prototype",
                  "Track usability, value perception, willingness to pay",
                  "Document insights systematically",
                ],
              },
              {
                title: "Validate Your Value Proposition",
                sub: [
                  "Use a Problem–Solution Fit Survey to test whether your solution resonates",
                  "Prioritize feedback on ease of use, value delivered, and unmet needs",
                  "Eliminate or delay non-critical features",
                ],
              },
              {
                title: "Plan the MVP Build",
                sub: [
                  "Decide: no-code MVP, freelance devs, or in-house team",
                  "Create a clear development backlog with prioritized features",
                  "Estimate cost, timeline, and resources",
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: "At this stage, your platform unlocks validation-oriented resources:",
          },
          {
            kind: 'bullets',
            items: [
              "MVP Validation Workbook: structured testing process",
              "Prototype Outsourcing Guide: tips for freelancers/agency builds",
              "Customer Feedback Tracker: capture and organize user insights",
              "Feature Prioritization Matrix: decide what goes into MVP vs later",
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: "Having a prototype is great, but remember: products don't fail because they can't be built, they fail because nobody wants them. This stage is about learning, not selling. Every test you run, every interview you do, every tweak you make brings you closer to an MVP that customers will use and love. Investors won't fund \"a prototype.\" They will fund evidence that the prototype solves a real problem.",
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: "You are a Prototype Starter.", tone: 'blue' },
          {
            kind: 'paragraph',
            text: "You've built something. Now it's time to validate it.",
          },
          {
            kind: 'bullets',
            items: [
              "Use the MVP Validation Workbook to structure customer testing",
              "Recruit 10 users for hands-on feedback",
              "Map your MVP backlog using the Feature Prioritization Matrix",
              "Book a Prototype Review Call with a Conncct advisor to refine your build plan",
            ],
          },
          {
            kind: 'callout',
            text: "Your next milestone: progress to G2R3 MVP Builder, where your prototype evolves into a functional MVP designed for real-world use.",
            tone: 'green',
          },
        ],
      },
    ],
  },
  {
    key: 'G2R3',
    group: 'G2',
    rCode: 'R3',
    pillar: 'Growth Architecture',
    stage: 'High Risk Stage',
    tone: 'orange',
    profile: 'MVP Builder',
    tagline: "Focused on turning ideas and prototypes into a working product that validates demand.",
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: "You've made a significant leap forward: you're no longer just testing a prototype. You are now building your MVP. This is a crucial transition because the MVP is your first chance to put a real product in the hands of real users and prove that your solution can deliver value. At this stage, however, risk remains high. An MVP that doesn't resonate with users or is built without focus can waste time and resources. The challenge is to prioritize ruthlessly and make sure the MVP answers one question: Does this solve the problem for the customer? You are an MVP Builder, focused on turning ideas and prototypes into a working product that validates demand.",
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          {
            kind: 'paragraph',
            text: "The diagnostic highlights the following signals:",
          },
          {
            kind: 'bullets',
            items: [
              "You've started (or are about to start) building an MVP.",
              "Some early users may be engaged, but usage is not systematic.",
              "Features may be too many or unfocused, diluting value.",
              "You haven't yet defined clear success metrics for your MVP.",
            ],
          },
          {
            kind: 'paragraph',
            text: "This means your build is progressing, but it is not yet strategic or validated.",
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: "Prioritize MVP Features",
                sub: [
                  "Define \"must-have\" vs. \"nice-to-have\"",
                  "Focus on 1–2 core features that test your value proposition",
                  "Resist scope creep: every feature should serve validation",
                ],
              },
              {
                title: "Set MVP Success Metrics",
                sub: [
                  "Decide what validates success: signups, daily use, retention, willingness to pay",
                  "Track these KPIs from day one",
                  "Use simple dashboards (Google Sheets, Airtable) to monitor",
                ],
              },
              {
                title: "Build Feedback Loops",
                sub: [
                  "Recruit 20–30 test users",
                  "Gather structured feedback weekly",
                  "Iterate quickly: fix usability issues, refine workflows",
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: "At this stage, your platform unlocks execution-focused tools:",
          },
          {
            kind: 'bullets',
            items: [
              "CTO Sprint Builder Lite: feature prioritization + sprint templates",
              "MVP Roadmap Template: structured timeline for build & validation",
              "Outsourcing Playbook: guidelines for working with dev agencies/freelancers",
              "Early KPI Tracker: track signups, retention, engagement",
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: "You're in the build phase, but building alone isn't the victory. The goal is validation: proving that people will use your product and that it solves their problem. The danger here is overbuilding. Too many founders waste months building features nobody asked for. The winners focus on speed, feedback, and iteration. If your MVP resonates, you'll be ready to attract investors, advisors, and your first paying customers.",
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: "You are an MVP Builder.", tone: 'blue' },
          {
            kind: 'paragraph',
            text: "You've moved past ideas and prototypes. Now you're proving your solution in the real world.",
          },
          {
            kind: 'bullets',
            items: [
              "Finalize your MVP scope using the CTO Sprint Builder Lite",
              "Recruit 20 test users and start tracking engagement",
              "Use the MVP Roadmap Template to structure your development timeline",
              "Book an MVP Strategy Call with a Conncct advisor to refine your validation plan",
            ],
          },
          {
            kind: 'callout',
            text: "Your next milestone: progress to G2R4 Product Launcher, where your MVP goes live with real users and early traction becomes visible.",
            tone: 'green',
          },
        ],
      },
    ],
  },
  {
    key: 'G2R4',
    group: 'G2',
    rCode: 'R4',
    pillar: 'Growth Architecture',
    stage: 'Medium Risk Stage',
    tone: 'yellow',
    profile: 'Product Launcher',
    tagline: "Ready to face the market and prove whether your solution resonates.",
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: "You're at a critical inflection point: your MVP is built and functional, and you are either preparing to launch or have just begun rolling it out to early customers. This is a moment of both excitement and risk: your product is about to meet the real market. The challenge now is to avoid \"build it and they will come\" thinking. A launch without structured testing, marketing, and traction planning risks wasted effort and missed learnings. Your priority is to turn your launch into a controlled experiment, generating insights and signals that will guide your next iteration. You are a Product Launcher, ready to face the market and prove whether your solution resonates.",
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          {
            kind: 'paragraph',
            text: "The diagnostic reveals several signals:",
          },
          {
            kind: 'bullets',
            items: [
              "Your MVP is functional and near market-ready.",
              "You may have pilot users or beta testers, but usage is not yet consistent.",
              "Early traction metrics (signups, engagement) may exist, but they are not validated at scale.",
              "You lack a structured go-to-market (GTM) plan beyond launch.",
            ],
          },
          {
            kind: 'paragraph',
            text: "This means your risk is no longer about \"can we build it?\" but \"will the market care?\"",
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: "Execute a Structured Beta Launch",
                sub: [
                  "Recruit 50–100 early adopters",
                  "Set clear success metrics (activation rate, retention, NPS)",
                  "Document learnings after each user cohort",
                ],
              },
              {
                title: "Build a Go-to-Market Plan",
                sub: [
                  "Define your first channel: organic (communities), paid ads, or partnerships",
                  "Craft your launch message: what problem do you solve, why now?",
                  "Allocate budget/time to marketing experiments",
                ],
              },
              {
                title: "Track & Iterate on Early Traction",
                sub: [
                  "Monitor usage metrics daily",
                  "Run weekly sprints to refine product based on user behavior",
                  "Begin documenting customer testimonials or case studies",
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: "Your platform now unlocks market entry toolkits:",
          },
          {
            kind: 'bullets',
            items: [
              "MVP Roadmap Pro Edition: structured rollout & iteration plan",
              "Beta Launch Playbook: recruitment scripts + success tracking",
              "Early Growth KPI Dashboard: template to monitor adoption & retention",
              "Messaging Framework Guide: refine positioning for GTM",
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: "You are about to test your business in the real world. This is where dreams meet data. The product launch is not the finish line, it is the starting line of learning. Success at this stage is measured not by revenue, but by traction signals: user adoption, engagement, and feedback. These insights will shape the next build and determine your readiness for scaling.",
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: "You are a Product Launcher.", tone: 'blue' },
          {
            kind: 'paragraph',
            text: "Your MVP is ready to meet the market. Now you must treat your launch as a structured experiment.",
          },
          {
            kind: 'bullets',
            items: [
              "Run a controlled beta launch with 50–100 users",
              "Use the Beta Launch Playbook to track early adoption",
              "Set up your Early Growth KPI Dashboard",
              "Book a Launch Strategy Session with a Conncct advisor to refine your GTM",
            ],
          },
          {
            kind: 'callout',
            text: "Your next milestone: progress to G2R5 Validated Builder, where your MVP proves market demand and traction becomes your strongest asset.",
            tone: 'green',
          },
        ],
      },
    ],
  },
  {
    key: 'G2R5',
    group: 'G2',
    rCode: 'R5',
    pillar: 'Growth Architecture',
    stage: 'Semi-Ready Stage',
    tone: 'blue',
    profile: 'Validated Builder',
    tagline: "You've earned early evidence that your product matters, and now your job is to turn early traction into repeatable traction.",
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: "Congratulations, you've crossed one of the hardest thresholds in the startup journey: your MVP is validated with real customers. You're not just launching; you're proving. This is a major milestone because it moves you from \"we built something\" to \"customers are using it and finding value.\" At this stage, your risk has shifted: it's less about product feasibility and more about refining, retaining, and preparing to scale. You are a Validated Builder, you've earned early evidence that your product matters, and now your job is to turn early traction into repeatable traction.",
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          {
            kind: 'paragraph',
            text: "Your diagnostic results show key signs of semi-readiness:",
          },
          {
            kind: 'bullets',
            items: [
              "Customers are actively using your MVP, beyond initial pilots.",
              "You have quantifiable traction signals: paying users, engagement, or retention.",
              "Your product delivers value but still has gaps in UX, stability, or scalability.",
              "Go-to-market is working in small bursts, but not yet repeatable.",
            ],
          },
          {
            kind: 'paragraph',
            text: "This places you in a strong position to attract early-stage investors, but only if you can prove your traction is repeatable and scalable.",
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: "Strengthen Product Retention",
                sub: [
                  "Focus on engagement and churn reduction",
                  "Run user interviews to uncover drop-off reasons",
                  "Double down on features that drive stickiness",
                ],
              },
              {
                title: "Refine Go-to-Market (GTM) Playbook",
                sub: [
                  "Document what acquisition channels are working",
                  "Build a repeatable process for customer onboarding",
                  "Test pricing models to optimize conversion",
                ],
              },
              {
                title: "Prepare for Scaling & Funding",
                sub: [
                  "Start building a scaling roadmap (tech, hiring, operations)",
                  "Translate traction into investor metrics (CAC, LTV, MRR/ARR growth)",
                  "Upgrade your pitch deck with validation data",
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: "At this stage, your platform unlocks scaling prep tools:",
          },
          {
            kind: 'bullets',
            items: [
              "MVP Builder Pro Toolkit: advanced sprint templates & retention metrics",
              "Growth Playbook Lite: first GTM frameworks + onboarding process design",
              "Traction-to-Investor Deck Guide: template for integrating KPIs into pitch decks",
              "Pricing Experimentation Template: simple model for testing different revenue strategies",
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: "You've de-risked your startup by proving that real customers care. That's a major accomplishment. The next challenge is to ensure this isn't a one-off validation but the start of a repeatable growth engine. Investors will take you more seriously now, but they'll expect proof that your traction can scale. Customers using your product is good; customers staying and paying consistently is great.",
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: "You are a Validated Builder.", tone: 'blue' },
          {
            kind: 'paragraph',
            text: "You've proven demand. Now it's time to refine and prepare for scale.",
          },
          {
            kind: 'bullets',
            items: [
              "Use the MVP Builder Pro Toolkit to refine retention metrics",
              "Document your GTM in the Growth Playbook Lite",
              "Upgrade your deck with traction KPIs using the Traction-to-Investor Deck Guide",
              "Book a Traction Review Call with a Conncct advisor to prepare for your next investor conversations",
            ],
          },
          {
            kind: 'callout',
            text: "Your next milestone: progress to G2R6 Scalable Builder, where your product, GTM, and traction mature into a foundation for growth and fundraising.",
            tone: 'green',
          },
        ],
      },
    ],
  },
  {
    key: 'G2R6',
    group: 'G2',
    rCode: 'R6',
    pillar: 'Growth Architecture',
    stage: 'Investor-Ready Stage',
    tone: 'green',
    profile: 'Scalable Builder',
    tagline: "A founder ready to move from early adopters to mainstream customers, and from \"startup\" to \"growth company.\"",
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: "You've moved beyond validation: your MVP is working, customers are using it, and you have evidence of traction. Now the challenge is to scale your product and GTM systematically. At this stage, you are no longer proving whether people want your product. You're proving you can deliver it reliably, repeatedly, and at scale. Investors now see you as an attractive prospect, provided you can show strong unit economics and a clear growth strategy. You are a Scalable Builder, a founder ready to move from early adopters to mainstream customers, and from \"startup\" to \"growth company.\"",
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          {
            kind: 'paragraph',
            text: "Your answers signal readiness for the next level:",
          },
          {
            kind: 'bullets',
            items: [
              "Validated MVP with active paying users, beyond pilot stage.",
              "Early repeatable acquisition channels, some consistency in GTM efforts.",
              "Engagement and retention improving, though still needing optimization.",
              "Basic financial metrics (CAC, LTV, runway) are being tracked.",
              "Investors would take you seriously, but expect a clear plan for scaling.",
            ],
          },
          {
            kind: 'paragraph',
            text: "This is the transition point from startup hustle to structured growth.",
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: "Strengthen Unit Economics",
                sub: [
                  "Track CAC, LTV, churn, and payback period rigorously",
                  "Focus on reducing CAC through channel optimization",
                  "Improve LTV via pricing, upsells, and customer success",
                ],
              },
              {
                title: "Scale Product Infrastructure",
                sub: [
                  "Ensure your tech is stable and scalable (cloud, APIs, automation)",
                  "Build processes for bug tracking, feature rollout, and QA",
                  "Prepare for higher user volume without breaking",
                ],
              },
              {
                title: "Build the Growth Engine",
                sub: [
                  "Document your GTM playbook (sales funnels, onboarding, customer journeys)",
                  "Begin hiring for growth (sales, marketing, customer success)",
                  "Allocate capital strategically between product and growth",
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: "Your platform now provides scaling-focused resources:",
          },
          {
            kind: 'bullets',
            items: [
              "CTO Sprint Builder Pro: advanced roadmap & scaling sprints",
              "GTM Growth Playbook: playbook for systematic customer acquisition",
              "Investor Metrics Dashboard: template to track CAC, LTV, MRR/ARR",
              "Hiring Plan Template: structure your first growth hires",
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: "You've de-risked the core question: customers want what you're building. Now your credibility with investors depends on showing that you can scale smartly. At this stage, messy execution can hold you back. Strong systems, clean metrics, and a disciplined GTM will make the difference between being seen as a \"startup with potential\" and a \"growth company worth backing.\"",
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: "You are a Scalable Builder.", tone: 'blue' },
          {
            kind: 'paragraph',
            text: "Your MVP has proven itself. Now it's about building the engine for growth.",
          },
          {
            kind: 'bullets',
            items: [
              "Track your unit economics with the Investor Metrics Dashboard",
              "Use the CTO Sprint Builder Pro to ensure product stability",
              "Document your GTM in the Growth Playbook for repeatability",
              "Book a Scaling Strategy Session with a Conncct advisor to refine your growth roadmap",
            ],
          },
          {
            kind: 'callout',
            text: "Your next milestone: progress to G2R7 Product Star, where your product, traction, and growth story become exceptional and investor demand starts chasing you.",
            tone: 'green',
          },
        ],
      },
    ],
  },
  {
    key: 'G2R7',
    group: 'G2',
    rCode: 'R7',
    pillar: 'Growth Architecture',
    stage: 'Exceptional Stage',
    tone: 'green',
    profile: 'Product Star',
    tagline: "A founder with a standout product, strong validation, and the ability to scale globally.",
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: "You've reached the top tier of product development and market validation. Your MVP has evolved into a product that customers love, adoption is accelerating, and your systems are strong enough to scale. At this stage, you're not just proving product–market fit. You're demonstrating market leadership potential. Investors will actively pursue you, not only because of your traction but also because of your ability to execute with excellence. You are a Product Star, a founder with a standout product, strong validation, and the ability to scale globally.",
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          {
            kind: 'paragraph',
            text: "The diagnostic shows you have reached the exceptional range because:",
          },
          {
            kind: 'bullets',
            items: [
              "Your product is validated and scaling: consistent user adoption, retention, and monetization.",
              "Traction is strong and accelerating: repeatable growth signals across multiple channels.",
              "Unit economics are solid: CAC, LTV, and churn are investor-grade.",
              "Tech and operations are stable: your product is resilient enough to handle growth.",
              "You are attracting investor interest, not just readiness, but demand.",
            ],
          },
          {
            kind: 'paragraph',
            text: "This is the stage where founders shift from being capital seekers to capital selectors.",
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: "Scale Strategically",
                sub: [
                  "Expand into new markets or customer segments",
                  "Localize product and GTM if targeting international markets",
                  "Ensure operations and support scale with growth",
                ],
              },
              {
                title: "Optimize Growth Leverage",
                sub: [
                  "Double down on the channels delivering the best ROI",
                  "Build referral programs, partnerships, and enterprise pipelines",
                  "Balance growth speed with customer satisfaction",
                ],
              },
              {
                title: "Align for Next-Stage Funding (Series A/B)",
                sub: [
                  "Prepare for institutional investor standards (advanced data rooms, governance, board structure)",
                  "Position your raise not just as capital, but as strategic acceleration",
                  "Highlight your traction story as a key differentiator",
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: "At this stage, your platform unlocks elite-level scaling and investor tools:",
          },
          {
            kind: 'bullets',
            items: [
              "Investor Growth Readiness Package: benchmarking against Series A/B expectations",
              "Market Expansion Playbook: step-by-step framework for international growth",
              "Scaling Governance Toolkit: board structure, compliance, and ESG readiness",
              "Strategic Partnership Framework: identify and negotiate win–win alliances",
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: "You've moved into rare territory: your product has not only proven demand but is showing signs of dominance. This is when investors will compete to back you. Your focus should now shift to choosing the right partners and building infrastructure for long-term scale. Over-optimizing valuation without considering strategic fit could slow you down, the right investors and partners can accelerate your trajectory far beyond the money itself.",
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: "You are a Product Star.", tone: 'blue' },
          {
            kind: 'paragraph',
            text: "Your product is validated, loved, and scaling. Investors want in. Now it's your turn to be strategic.",
          },
          {
            kind: 'bullets',
            items: [
              "Use the Market Expansion Playbook to explore scaling opportunities",
              "Benchmark your company against Series A/B investor standards with the Growth Readiness Package",
              "Build a strategic investor short list (focus on capital + partnerships)",
              "Book a Global Growth Strategy Session with a Conncct advisor to design your next-stage roadmap",
            ],
          },
          {
            kind: 'callout',
            text: "Your next frontier: becoming a market leader, scaling internationally, and securing top-tier funding that accelerates your vision.",
            tone: 'green',
          },
        ],
      },
    ],
  },
];
